import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, Sparkles } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { TrialUnlockPrompt, useTrialUnlocks } from '@/hooks/useTrialUnlocks'

const FEATURE_LABEL: Record<string, string> = {
  auto_callback: 'Auto Callback',
  auto_follow_up: 'Auto Follow-Up',
  reminder_calls: 'Reminder Calls',
  outreach_lists: 'Outreach Lists',
  ai_outbound_agent: 'Outbound Agent',
  crm_integrations: 'CRM Integrations',
}

export interface UnlockPromptDialogProps {
  prompt: TrialUnlockPrompt | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * One-prompt-at-a-time dialog for Principal feature unlocks during trial.
 * Wired globally by the UnlockPromptProvider; shows the first pending prompt
 * until the user acts on it (enable or dismiss).
 */
export function UnlockPromptDialog({ prompt, open, onOpenChange }: UnlockPromptDialogProps) {
  const { enable, dismiss } = useTrialUnlocks({ enabled: false }) // parent owns polling
  const [enabling, setEnabling] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const { toast } = useToast()

  if (!prompt) return null

  const handleEnable = async () => {
    setEnabling(true)
    try {
      await enable(prompt.feature)
      toast({
        title: `${FEATURE_LABEL[prompt.feature] ?? prompt.feature} unlocked`,
        description: 'Now available for the rest of your trial.',
      })
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'Could not unlock feature',
        description: err.response?.data?.message ?? err.message ?? 'Try again in a moment.',
        variant: 'destructive',
      })
    } finally {
      setEnabling(false)
    }
  }

  const handleDismiss = async () => {
    setDismissing(true)
    try {
      await dismiss(prompt.id)
      onOpenChange(false)
    } catch {
      /* ignore — dismissal is best-effort */
    } finally {
      setDismissing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3 mb-2">
            <div className="shrink-0 h-10 w-10 rounded-lg bg-brand-periwinkle/15 border border-brand-periwinkle/30 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-brand-royal dark:text-brand-periwinkle" strokeWidth={2} />
            </div>
            <div>
              <DialogTitle className="text-lg font-heading leading-tight">{prompt.headline}</DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed">
                {prompt.body}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="rounded-xl border border-brand-periwinkle/20 bg-brand-periwinkle/5 p-3 text-xs text-foreground/80 flex items-start gap-2">
          <Sparkles className="h-3.5 w-3.5 text-brand-royal dark:text-brand-periwinkle shrink-0 mt-0.5" strokeWidth={2} />
          <span>
            <span className="font-semibold text-foreground">{FEATURE_LABEL[prompt.feature] ?? prompt.feature}</span> is a Principal feature. It's yours free for the rest of your trial — no charge, no commitment.
          </span>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleDismiss}
            disabled={enabling || dismissing}
            className="text-muted-foreground"
          >
            {dismissing ? 'Not now…' : 'Not now'}
          </Button>
          <Button
            type="button"
            onClick={handleEnable}
            disabled={enabling || dismissing}
            className="flex-1 bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:opacity-90 text-white shadow-md shadow-brand-periwinkle/20"
          >
            {enabling ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Turning on…
              </>
            ) : (
              prompt.cta
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export interface UnlockPromptHostProps {
  /**
   * Render the globally-mounted prompt host. Uses its own useTrialUnlocks
   * instance for polling — do not nest inside another useTrialUnlocks consumer
   * that passes `enabled: false` to this host.
   */
  enabled?: boolean
}

/**
 * Global host — mount once in DashboardLayout. Polls for prompts and renders
 * one at a time. Marks each prompt as "shown" on first render to prevent
 * re-prompting on every poll cycle.
 */
export function UnlockPromptHost({ enabled = true }: UnlockPromptHostProps) {
  const { state, refresh } = useTrialUnlocks({ enabled })
  const [activePrompt, setActivePrompt] = useState<TrialUnlockPrompt | null>(null)
  const [open, setOpen] = useState(false)

  // Show the first pending prompt when state updates.
  // Server marks prompts as "shown" when returning them via /trial-unlocks,
  // so we don't need a client-side markShown here — that's atomic on the
  // server and avoids the re-surface-on-tab-close bug.
  useEffect(() => {
    if (!enabled) return
    if (!state.isTrialing) {
      setActivePrompt(null)
      setOpen(false)
      return
    }
    if (state.prompts.length === 0) {
      if (open && !activePrompt) setOpen(false)
      return
    }
    if (!activePrompt) {
      const next = state.prompts[0]
      setActivePrompt(next)
      setOpen(true)
    }
  }, [state.prompts, state.isTrialing, enabled, activePrompt, open])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      // Clear the active prompt after close animation starts
      setTimeout(() => {
        setActivePrompt(null)
        refresh() // pick up the next prompt if any
      }, 200)
    }
  }

  if (!activePrompt) return null
  return <UnlockPromptDialog prompt={activePrompt} open={open} onOpenChange={handleOpenChange} />
}
